/**
 * Indicator scope binding — a thin wrapper over the shared `useScopedCascade`
 * engine (tools/_shared). It only supplies WHERE indicator settings live (the
 * per-tab + user slots) and HOW to write / persist / clone them; all the scope
 * logic (scope dropdown, inherit/Customize, Copy from User / Reset / Remove,
 * Cancel rollback, Save, the ⋯-menu actions) lives in the generic hook and is
 * shared with the Fibonacci + Chart Properties dialogs.
 *
 * Note: indicators use user scope as a non-cascading TEMPLATE — a chart with no
 * tab override renders the built-in default, NOT the user value (see
 * `selectEffectiveIndicators`). That read-time choice is independent of this
 * edit-time hook.
 */
import { useAppDispatch, useAppSelector, useActiveTabId } from "@/state/hooks";
import {
  setIndicators,
  DEFAULT_INDICATORS,
  type IndicatorSettings,
} from "@/state/slices/uiSlice";
import {
  setUserIndicators,
  saveUserIndicators,
} from "@/state/slices/userPrefsSlice";
import {
  useScopedCascade,
  type ScopedCascade,
} from "@/tools/_shared/useScopedCascade";

export type ScopedIndicators = ScopedCascade<IndicatorSettings>;

// Deep-ish clone so a seeded override never shares the maLines objects (or RSI
// refs) with the User template / default it was copied from.
const cloneIndicators = (ind: IndicatorSettings): IndicatorSettings => ({
  ...ind,
  maLines: ind.maLines.map((l) => ({ ...l })),
  rsi: { ...ind.rsi },
  anchorRsi: ind.anchorRsi ? { ...ind.anchorRsi } : ind.anchorRsi,
});

export function useScopedIndicators(open: boolean): ScopedIndicators {
  const dispatch = useAppDispatch();
  const tabId = useActiveTabId();
  const saving = useAppSelector((s) => s.userPrefs.saving);
  const tabValue = useAppSelector((s) => s.ui.byTab[tabId]?.indicators ?? null);
  const userValue = useAppSelector((s) => s.userPrefs.indicators);

  return useScopedCascade<IndicatorSettings>(
    {
      tabValue,
      userValue,
      defaultValue: DEFAULT_INDICATORS,
      writeTab: (v) => {
        dispatch(setIndicators({ tabId, indicators: v }));
      },
      writeUser: (v) => {
        dispatch(setUserIndicators(v));
      },
      persistUser: async (v) => {
        await dispatch(saveUserIndicators({ settings: v }));
      },
      clone: cloneIndicators,
      saving,
      removeOverrideTitle:
        "Remove this chart's indicators (User scope is a template, not applied)",
    },
    open,
  );
}
